import React, { useEffect, useMemo, useRef, useState } from "react";
import { format, addDays, startOfWeek, endOfWeek, isToday } from "date-fns";
import { formatDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as UiCalendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, ChevronsUpDown, Calendar, CalendarRange, RefreshCw } from "lucide-react";
import type { FleetVehicleType, FleetViewMode } from "@/components/fleet-capacity/types";
import { useFleetCapacityData, useFleetCapacityDataForAllTypes, NZ_WEEK_STARTS_ON, STS_VEHICLE_TYPE, TTC_VEHICLE_TYPE } from "@/components/fleet-capacity/utils";
import { loadVehicleTypes, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";
import FleetSlotSheet, { type FleetSlotSheetHandle } from "@/components/fleet-capacity/FleetSlotSheet";
import FleetWeekSummary from "@/components/fleet-capacity/FleetWeekSummary";
import BreaksTab from "@/components/fleet-capacity/BreaksTab";
import MultiLegReview from "@/components/fleet-capacity/MultiLegReview";
import { useBreakConflictAutoPush } from "@/components/fleet-capacity/useBreakConflictAutoPush";

const BREAKS_TAB_KEY = "__breaks__";
const STS_TAB_KEY = "__sts__";
const TTC_TAB_KEY = "__ttc__";
const MULTI_LEG_TAB_KEY = "__multileg__";

interface TypeGroup {
  key: string;
  label: string;
  types: VehicleTypeRow[];
}

/**
 * Groups vehicle types for the Day tabs by icon_style rather than a
 * hardcoded name list -- today that means Sedan+SUV (both "car") share one
 * tab and Wheelchair (the only "truck") gets its own, but it stays correct
 * if another car-like or truck-like type is added later.
 */
const buildTypeGroups = (vehicleTypes: VehicleTypeRow[]): TypeGroup[] => {
  const byStyle = new Map<string, VehicleTypeRow[]>();
  for (const t of vehicleTypes) {
    if (!byStyle.has(t.icon_style)) byStyle.set(t.icon_style, []);
    byStyle.get(t.icon_style)!.push(t);
  }
  return Array.from(byStyle.entries())
    .map(([key, types]) => ({ key, label: types.map((t) => t.vehicle_type).join(" & "), types }))
    .sort((a, b) => Math.min(...a.types.map((t) => t.sort_order)) - Math.min(...b.types.map((t) => t.sort_order)));
};

const FleetCapacityViewPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<FleetViewMode>("day");
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);
  const [vehicleTypesLoading, setVehicleTypesLoading] = useState(true);
  const [vehicleTypesError, setVehicleTypesError] = useState<string | null>(null);
  const [activeVehicleType, setActiveVehicleType] = useState<FleetVehicleType>("");
  const [activeDayGroupKey, setActiveDayGroupKey] = useState<string>("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const slotSheetRef = useRef<FleetSlotSheetHandle>(null);

  const loadTypes = () => {
    setVehicleTypesLoading(true);
    setVehicleTypesError(null);
    loadVehicleTypes()
      .then((types) => {
        setVehicleTypes(types);
        setActiveVehicleType((current) => current || types[0]?.vehicle_type || "");
        // loadVehicleTypes() swallows its own errors (logs and returns []),
        // so an empty result here is the only signal something's wrong --
        // most often a stale/expired login session rather than genuinely
        // zero vehicle types configured. Without this, the page below is
        // stuck showing "Loading..." forever with no way to tell why.
        if (types.length === 0) {
          setVehicleTypesError(
            "Couldn't load vehicle types. Try refreshing the page -- if that doesn't fix it, log out and back in."
          );
        }
      })
      .finally(() => setVehicleTypesLoading(false));
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const typeGroups = useMemo(() => buildTypeGroups(vehicleTypes), [vehicleTypes]);

  // STS and TTC are cross-cutting views (which vehicle/driver is working an
  // STS_*/TTC_* shift, plus anything TaxiCaller tags with that company name)
  // rather than real vehicle_types, so they're appended here for
  // data-fetching purposes only -- never written into vehicle_types, and
  // kept out of typeGroups so each gets its own tab instead of merging into
  // Sedan/SUV.
  const allDisplayTypes = useMemo(() => [...vehicleTypes, STS_VEHICLE_TYPE, TTC_VEHICLE_TYPE], [vehicleTypes]);

  useEffect(() => {
    if (!activeDayGroupKey && typeGroups.length > 0) setActiveDayGroupKey(typeGroups[0].key);
  }, [typeGroups, activeDayGroupKey]);

  const activeGroup = typeGroups.find((g) => g.key === activeDayGroupKey) || null;

  const { dataByType, loading: dayLoading, error: dayError, reload: reloadDay } = useFleetCapacityDataForAllTypes(currentDate, allDisplayTypes);
  const { weekSummaries, loading: weekLoading, error: weekError } = useFleetCapacityData(currentDate, "week", activeVehicleType);

  const dateStr = format(currentDate, "yyyy-MM-dd");

  // Must run regardless of which sub-tab is showing -- a real booking can
  // land on a published break at any time, and dispatchers mostly watch
  // the main slot sheet, not the Breaks tab. See useBreakConflictAutoPush.
  const stuckBreakShiftIds = useBreakConflictAutoPush(dataByType, dateStr);

  const goBack = () => setCurrentDate((d) => addDays(d, viewMode === "day" ? -1 : -7));
  const goForward = () => setCurrentDate((d) => addDays(d, viewMode === "day" ? 1 : 7));
  const goToday = () => setCurrentDate(new Date());

  // Manually re-fires the Forward Jobs report pull for the day being viewed
  // (there's no update webhook for it yet -- see sync-forward-jobs-from-
  // taxicaller -- only a 15-min cron for the *current* month) so a
  // dispatcher can pull in a change right now instead of waiting.
  const [refreshingBookings, setRefreshingBookings] = useState(false);
  const handleRefreshBookings = async () => {
    setRefreshingBookings(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-forward-jobs-from-taxicaller", {
        body: { start_date: dateStr, end_date: dateStr },
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data.error || "Sync failed");
      toast({
        title: "Bookings refreshed",
        description: `${dateStr}: ${data.inserted} new, ${data.updated} updated${data.failed ? `, ${data.failed} failed` : ""}.`,
      });
      reloadDay();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to refresh bookings";
      toast({ title: "Refresh failed", description: message, variant: "destructive" });
    } finally {
      setRefreshingBookings(false);
    }
  };

  const handleDatePicked = (date: Date | undefined) => {
    if (!date) return;
    setCurrentDate(date);
    setDatePickerOpen(false);
  };

  const datePickerTitle =
    viewMode === "day"
      ? `${format(currentDate, "EEEE")} • ${formatDate(currentDate)}`
      : `${formatDate(startOfWeek(currentDate, { weekStartsOn: NZ_WEEK_STARTS_ON }))} – ${formatDate(
          endOfWeek(currentDate, { weekStartsOn: NZ_WEEK_STARTS_ON })
        )}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white">Fleet Capacity</h2>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-gray-800 border border-gray-700 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode("day")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                viewMode === "day" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <Calendar className="h-3.5 w-3.5" /> Day
            </button>
            <button
              onClick={() => setViewMode("week")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                viewMode === "week" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <CalendarRange className="h-3.5 w-3.5" /> Week
            </button>
          </div>

          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[210px] justify-between">
                <span>{datePickerTitle}</span>
                <ChevronsUpDown className="h-4 w-4 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3 bg-gray-900 border-gray-700" align="end">
              <UiCalendar
                mode="single"
                selected={currentDate}
                onSelect={handleDatePicked}
                initialFocus
                weekStartsOn={NZ_WEEK_STARTS_ON}
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="sm" onClick={goBack}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goForward}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {viewMode === "day" && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshBookings}
              disabled={refreshingBookings}
              title="Re-pull TaxiCaller's forward bookings report for this day"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshingBookings ? "animate-spin" : ""}`} />
              Refresh Bookings
            </Button>
          )}
        </div>
      </div>

      {vehicleTypesLoading ? (
        <p className="text-sm text-gray-400">Loading vehicle types...</p>
      ) : vehicleTypesError ? (
        <div className="rounded-md border border-red-500/40 bg-red-950/20 p-4 space-y-3">
          <p className="text-sm text-red-400">{vehicleTypesError}</p>
          <Button variant="outline" size="sm" onClick={loadTypes}>
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Try Again
          </Button>
        </div>
      ) : (
        <>
      {viewMode === "week" && vehicleTypes.length > 0 && (
        <Tabs value={activeVehicleType} onValueChange={(v) => setActiveVehicleType(v as FleetVehicleType)}>
          <TabsList>
            {allDisplayTypes.map((t) => (
              <TabsTrigger key={t.vehicle_type} value={t.vehicle_type}>
                {t.vehicle_type}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {viewMode === "day" && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              {typeGroups.length > 0 && (
                <Tabs value={activeDayGroupKey} onValueChange={setActiveDayGroupKey}>
                  <TabsList>
                    {typeGroups.map((g) => (
                      <TabsTrigger key={g.key} value={g.key}>
                        {g.label}
                      </TabsTrigger>
                    ))}
                    <TabsTrigger value={STS_TAB_KEY}>STS</TabsTrigger>
                    <TabsTrigger value={TTC_TAB_KEY}>TTC</TabsTrigger>
                    <TabsTrigger value={BREAKS_TAB_KEY}>Breaks</TabsTrigger>
                    <TabsTrigger value={MULTI_LEG_TAB_KEY}>Multi-Leg</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </div>
            {isToday(currentDate) && activeDayGroupKey !== BREAKS_TAB_KEY && (
              <button
                type="button"
                onClick={() => slotSheetRef.current?.jumpToNow()}
                className="rounded-full border border-red-500 bg-gray-900/95 px-2.5 py-1 text-[11px] font-semibold text-red-400 shadow hover:bg-red-500 hover:text-white"
              >
                ● Jump to Now
              </button>
            )}
          </div>
          {dayError && <p className="text-sm text-red-400">{dayError}</p>}
          {activeDayGroupKey === MULTI_LEG_TAB_KEY ? (
            <MultiLegReview dateStr={dateStr} onGroupConfirmed={reloadDay} />
          ) : activeDayGroupKey === BREAKS_TAB_KEY ? (
            dayLoading ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : (
              !dayError && <BreaksTab dataByType={dataByType} dateStr={dateStr} stuckShiftIds={stuckBreakShiftIds} />
            )
          ) : activeDayGroupKey === STS_TAB_KEY ? (
            <>
              {dayLoading && <p className="text-sm text-gray-400">Loading...</p>}
              {!dayLoading && !dayError && (
                <FleetSlotSheet
                  ref={slotSheetRef}
                  dataByType={dataByType}
                  vehicleTypes={[STS_VEHICLE_TYPE]}
                  dateStr={dateStr}
                  enableUnassignedPlacement={false}
                />
              )}
            </>
          ) : activeDayGroupKey === TTC_TAB_KEY ? (
            <>
              {dayLoading && <p className="text-sm text-gray-400">Loading...</p>}
              {!dayLoading && !dayError && (
                <FleetSlotSheet
                  ref={slotSheetRef}
                  dataByType={dataByType}
                  vehicleTypes={[TTC_VEHICLE_TYPE]}
                  dateStr={dateStr}
                  enableUnassignedPlacement={false}
                />
              )}
            </>
          ) : (
            <>
              {(dayLoading || !activeGroup) && <p className="text-sm text-gray-400">Loading...</p>}
              {!dayLoading && !dayError && activeGroup && (
                <FleetSlotSheet ref={slotSheetRef} dataByType={dataByType} vehicleTypes={activeGroup.types} dateStr={dateStr} />
              )}
            </>
          )}
        </>
      )}

      {viewMode === "week" && (
        <>
          {weekError && <p className="text-sm text-red-400">{weekError}</p>}
          {(weekLoading || !activeVehicleType) && <p className="text-sm text-gray-400">Loading...</p>}
          {!weekLoading && !weekError && activeVehicleType && (
            <FleetWeekSummary
              weekSummaries={weekSummaries}
              onSelectDay={(d) => {
                setCurrentDate(new Date(d + "T00:00:00"));
                setViewMode("day");
              }}
            />
          )}
        </>
      )}
        </>
      )}
    </div>
  );
};

export default FleetCapacityViewPage;
